import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-headless";
const TITLE = "Shopify Headless & Hydrogen: wann es sich lohnt";
const DESCRIPTION =
  "Shopify Headless mit Hydrogen oder Storefront API: Aufwand, Betriebskosten und wann ein optimiertes Theme die bessere Entscheidung ist. Ehrliche Einordnung mit Kostenrahmen.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Shopify Headless bedeutet: Shopify liefert über die Storefront API nur noch Daten, das sichtbare Schaufenster wird als eigene Anwendung gebaut, etwa mit Hydrogen oder Next.js. Das lohnt sich bei sehr hohen Anforderungen an Performance, individuelle Kundenerlebnisse oder mehrere Ausgabekanäle. Für die meisten deutschen Shops ist ein gut optimiertes Theme schneller, günstiger und einfacher zu betreuen.";

const faqs = [
  {
    q: "Was ist Shopify Headless?",
    a: "Beim Headless-Betrieb bleibt Shopify das System für Produkte, Warenkorb und Checkout, die Darstellung im Browser kommt aber aus einer eigenen Frontend-Anwendung. Die Verbindung läuft über die Storefront API.",
  },
  {
    q: "Was ist Shopify Hydrogen?",
    a: "Hydrogen ist Shopifys eigenes React-Framework für Headless-Storefronts und wird typischerweise auf Oxygen, der Shopify-eigenen Hosting-Plattform, betrieben. Alternativen wie Next.js auf anderem Hosting sind möglich.",
  },
  {
    q: "Was kostet ein Headless-Shop?",
    a: "Der Aufbau beginnt realistisch im mittleren fünfstelligen Bereich, dazu kommt laufende Betreuung des Frontends. Ein gleichwertig schneller Standard-Shop liegt oft bei einem Bruchteil davon.",
  },
  {
    q: "Ist Headless besser für SEO?",
    a: "Nicht automatisch. Headless kann schneller sein, verlagert aber Verantwortung für Rendering, Meta-Steuerung und Indexierung in die eigene Anwendung. Ein gut gebautes Theme erreicht vergleichbare Werte mit deutlich weniger Risiko.",
  },
  {
    q: "Wann raten Sie von Headless ab?",
    a: "Wenn der Shop mit Standardfunktionen gut bedient ist, das Team klein ist und niemand das Frontend dauerhaft pflegt. Headless ohne eigenes Entwicklungsbudget wird zum teuren Risiko.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-headless")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Entwicklung",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Entwicklung"
      title="Shopify Headless und Hydrogen: die ehrliche Einordnung"
      intro="Headless ist das Schlagwort, mit dem Aufträge teuer werden. Manchmal zu Recht. Hier steht, was die Architektur wirklich bringt, was sie kostet und für wen ein schnelles Standard-Theme die klügere Wahl ist."
      source="ratgeber-shopify-headless"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Was sich hinter Headless verbirgt",
          p: [
            "Im Standard-Betrieb rendert Shopify das Theme selbst und liefert fertige Seiten aus. Beim Headless-Betrieb liefert die Storefront API nur Daten: Produkte, Preise, Warenkorb. Eine eigene Frontend-Anwendung, häufig mit Hydrogen oder Next.js gebaut, kümmert sich um Darstellung und Verhalten.",
            "Diese Trennung gibt volle Gestaltungsfreiheit und kann sehr schnelle Ladezeiten liefern. Sie macht Sie aber auch zum Betreiber einer eigenen Webanwendung mit allem, was dazugehört.",
          ],
        },
        {
          h: "Wann sich Headless wirklich lohnt",
          p: [
            "Sinnvoll wird die Architektur bei Anforderungen, die ein Theme nicht leisten kann.",
          ],
          list: [
            "Sehr individuelle Kundenerlebnisse, etwa Konfiguratoren mit komplexer Logik",
            "Mehrere Ausgabekanäle aus einem Datenbestand, etwa Shop, App und Kiosk",
            "Internationale Setups mit hohen Anforderungen an Ladezeit in vielen Regionen",
            "Bestehendes Entwicklungsteam, das ein Frontend dauerhaft betreuen kann",
          ],
        },
        {
          h: "Was die meisten Shops stattdessen brauchen",
          p: [
            "Die Ladezeit- und Gestaltungsziele, mit denen Headless verkauft wird, erreicht ein sauber gebautes Theme in den meisten Fällen ebenfalls: entschlackte Apps, optimierte Bilder, aufgeräumtes Liquid und sauberes CSS.",
            "Der Unterschied liegt im Budget: Ein optimiertes Theme kostet einen Bruchteil, braucht kein eigenes Hosting und bleibt für Ihr Team im Shopify-Admin pflegbar. Entwicklung im Theme berechne ich mit 110 € netto pro Stunde.",
          ],
        },
        {
          h: "Kosten und Betrieb im Vergleich",
          p: [
            "Ein Headless-Aufbau beginnt realistisch im mittleren fünfstelligen Bereich, dazu kommen Hosting, Monitoring und laufende Pflege des Frontends. Shopify Plus ist dafür keine Pflicht, wird aber häufig Teil solcher Projekte.",
            "Ein Standard-Shop mit optimiertem Theme liegt je nach Umfang bei 3.000 bis 12.000 € netto und bleibt ohne eigenes Entwicklungsteam betreibbar. Diese Größenordnung sollten Sie kennen, bevor ein Headless-Angebot auf dem Tisch liegt.",
          ],
        },
        {
          h: "Meine Empfehlung in einem Satz",
          p: [
            "Beginnen Sie mit der Frage, welche konkrete Anforderung das Theme nicht kann. Gibt es keine klare Antwort, ist Headless ein Statussymbol und kein Werkzeug. Ich prüfe das gern in einem kurzen Gespräch, bevor Sie Budget festlegen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-plus", label: "Shopify Plus im Überblick" },
        { to: "/shopify-app-entwicklung", label: "Shopify App Entwicklung" },
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung und Entwicklung" },
        { to: "/ratgeber/shopify-liquid", label: "Shopify Liquid verstehen" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
